import { FormikProps } from "formik";
import { useKodeListe, useSelectableKodeListe } from "../../hooks/useQueries";
import ValidationErrorMessage from "./ValidationErrorMessage";
import SuperDropdown from "../dropdown/SuperDropdown";
import DefaultDropdownItem from "../dropdown/DefaultDropdownItem";
import Spinner from "../spinner/Spinner";
import DefaultLoadingError from "../error/DefaultLoadingError";
import CloseIcon from "../icons/CloseIcon";
import IsRequired from "./common/IsRequired";
import { useEffect, useRef, useState } from "react";

export function KodeSelect({
  kodeListeType,
  onChange,
  placeholder,
  classNameContainer,
  clearAfterSelection,
  disabled,
  filterKeys,
  label,
  value,
  isRequired,
  focusInputOnMount,
  filter,
  excludeDeaktiveret,
}: {
  kodeListeType: KodeListeType;
  onChange: (item: KodeListe) => void;
  placeholder: string;
  classNameContainer?: string;
  clearAfterSelection?: boolean;
  disabled?: boolean;
  filterKeys?: Array<keyof KodeLister[KodeListeType][number]>;
  label?: string;
  value?: string;
  isRequired?: boolean;
  focusInputOnMount?: boolean;
  filter?: string[];
  excludeDeaktiveret?: boolean;
}) {
  const {
    kodeliste: initialKodeliste,
    getKodelisteError,
    getKodelisteIsLoading,
  } = useSelectableKodeListe(kodeListeType, { excludeDeaktiveret });

  if (getKodelisteIsLoading) {
    return (
      <Spinner
        inline
        className="form-control justify-content-start"
        label="Henter data"
        size={14}
      />
    );
  }
  if (getKodelisteError) {
    return <DefaultLoadingError label={String(getKodelisteError)} />;
  }
  const kodeliste = filter
    ? initialKodeliste?.filter((e) => filter.find((s) => s !== e.id))
    : initialKodeliste;
  if (!kodeliste) return <DefaultLoadingError label="Ingen kodeliste" />;

  kodeliste.sort((a, b) => a.navn.localeCompare(b.navn));

  const initialValue = kodeliste.find((obj) => obj.navn === value)?.navn;
  return (
    <>
      {label && (
        <label className="form-label">
          {label}
          {isRequired && <IsRequired />}
        </label>
      )}
      <SuperDropdown
        classNameContainer={classNameContainer}
        clearInputAfterSelection={clearAfterSelection}
        disabled={disabled}
        items={kodeliste}
        itemsIdKey={"navn"}
        filterKeys={filterKeys}
        itemToString={(item) => item?.navn ?? ""}
        placeholder={placeholder}
        initialInputValue={initialValue}
        onSelectedItemEvent={(event) => {
          if (event.selectedItem) {
            onChange(event.selectedItem);
          }
        }}
        itemToElement={(item) => <DefaultDropdownItem label={item.navn} />}
        focusInputOnMount={focusInputOnMount}
      />
    </>
  );
}
export function FormikKodeSelect<U>(props: {
  config: IKodeSelect<U>;
  formik: FormikProps<any>;
  value: string | undefined;
  onChange: (item: KodeListe | undefined) => void;
  clearAfterSelection?: boolean;
  disabled?: boolean;
}) {
  const { config, formik, onChange, value, clearAfterSelection, disabled } =
    props;
  const {
    label,
    placeholder,
    kodeListeType,
    isRequired,
    filter,
    excludeDeaktiveret,
  } = config;
  const [shouldFocusInputsOnMount, setShouldFocusInputsOnMount] =
    useState(false);

  const { kodeliste, getKodelisteError, getKodelisteIsLoading } =
    useKodeListe(kodeListeType);
  // check things are okay
  if (getKodelisteIsLoading) {
    return <Spinner inline label="Henter data" size={16} />;
  }
  if (getKodelisteError) {
    return <DefaultLoadingError label={String(getKodelisteError)} />;
  }
  if (!kodeliste) return <DefaultLoadingError label="Ingen kodeliste" />;
  if (value) {
    return (
      <>
        <ValueButton
          onClick={() => {
            setShouldFocusInputsOnMount(true);
            onChange(undefined);
          }}
          kodeliste={kodeliste}
          label={label}
          isRequired={isRequired}
          value={value}
          shouldFocusInputsOnMount={shouldFocusInputsOnMount}
          disabled={disabled}
        />
        <ValidationErrorMessage
          touched={formik.touched[config.id]}
          errorMessage={(formik.errors as any)[config.id]}
        />
      </>
    );
  }
  return (
    <>
      <KodeSelect
        isRequired={isRequired}
        onChange={(item) => {
          setShouldFocusInputsOnMount(true);
          onChange(item);
        }}
        value={value ? value : undefined}
        clearAfterSelection={clearAfterSelection}
        disabled={disabled}
        label={label}
        kodeListeType={kodeListeType}
        placeholder={placeholder}
        classNameContainer={`w-100${
          formik.touched[config.id] && (formik.errors as any)[config.id]
            ? " is-invalid"
            : ""
        }`}
        focusInputOnMount={shouldFocusInputsOnMount}
        filter={filter}
        excludeDeaktiveret={excludeDeaktiveret}
      />
      <ValidationErrorMessage
        touched={formik.touched[config.id]}
        errorMessage={(formik.errors as any)[config.id]}
      />
    </>
  );
}

function ValueButton(props: {
  label: string;
  isRequired?: boolean;
  kodeliste: KodeListe[];
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  buttonRef?: React.RefObject<HTMLButtonElement>;
  value: string | undefined;
  shouldFocusInputsOnMount?: boolean;
  disabled?: boolean;
}) {
  const {
    kodeliste,
    label,
    onClick,
    isRequired,
    value,
    shouldFocusInputsOnMount,
    disabled,
  } = props;
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (shouldFocusInputsOnMount && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [shouldFocusInputsOnMount]);
  const name = kodeliste.find((k) => {
    if (k.navn === value) return true;
    return k.type === "enum" ? k.id === value : k.nr.toString() === value;
  })?.navn;
  return (
    <>
      {label && (
        <label className="form-label">
          {label}
          {isRequired && <IsRequired />}
        </label>
      )}
      {disabled ? (
        <input value={name} disabled={disabled} className="form-control" />
      ) : (
        <button
          className="c-dropdown-super form-control"
          onClick={onClick}
          ref={buttonRef}
          disabled={disabled}
        >
          <span>{name}</span>
          <span className="ms-auto text-muted d-flex align-items-center">
            <CloseIcon height={18} />
          </span>
        </button>
      )}
    </>
  );
}
